import React, { useState } from 'react';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel,
  SelectTrigger, SelectValue,
} from './ui/select';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { PencilSimple, Check, X } from '@phosphor-icons/react';
import { BTP_PHASES, PHASE_MAP, getPhasesGrouped } from '../constants/btpPhases';

const CUSTOM_VALUE = '__custom__';

/**
 * Selecteur de phase BTP enrichi.
 * Combine le catalogue officiel (groupes) + champ libre pour phase personnalisee.
 *
 * @param {string} value         - phase courante (cle catalogue OU texte libre)
 * @param {(v:string) => void} onChange
 * @param {string} placeholder
 * @param {string} testIdPrefix
 */
export function PhaseSelector({ value, onChange, placeholder = 'Choisir une phase', testIdPrefix = 'phase' }) {
  const grouped = getPhasesGrouped();
  const isInCatalog = !!PHASE_MAP[value];
  const [customMode, setCustomMode] = useState(!!value && !isInCatalog);
  const [customInput, setCustomInput] = useState(isInCatalog ? '' : (value || ''));

  const handleSelectChange = (v) => {
    if (v === CUSTOM_VALUE) {
      setCustomMode(true);
      setCustomInput('');
      return;
    }
    setCustomMode(false);
    onChange(v);
  };

  const handleCustomConfirm = () => {
    const v = (customInput || '').trim();
    if (v.length === 0) return;
    onChange(v);
    setCustomMode(false);
  };

  const handleCustomCancel = () => {
    setCustomMode(false);
    setCustomInput('');
  };

  // Mode champ libre
  if (customMode) {
    return (
      <div className="flex gap-2 mt-1">
        <Input
          value={customInput}
          onChange={(e) => setCustomInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCustomConfirm(); } }}
          placeholder="Nom de la phase personnalisee"
          className="h-14 flex-1"
          data-testid={`${testIdPrefix}-custom-input`}
          autoFocus
        />
        <Button type="button" onClick={handleCustomConfirm}
          className="h-14 bg-emerald-600 hover:bg-emerald-700"
          data-testid={`${testIdPrefix}-custom-confirm`}
          title="Valider la phase personnalisee">
          <Check size={20} weight="bold" />
        </Button>
        <Button type="button" onClick={handleCustomCancel} variant="outline"
          className="h-14"
          data-testid={`${testIdPrefix}-custom-cancel`}
          title="Revenir au catalogue">
          <X size={20} weight="bold" />
        </Button>
      </div>
    );
  }

  // Mode catalogue
  return (
    <div className="flex gap-2 mt-1">
      <Select value={value || ''} onValueChange={handleSelectChange}>
        <SelectTrigger className="h-14 flex-1" data-testid={`${testIdPrefix}-select`}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent position="popper" className="z-[100] max-h-[400px]">
          {Object.entries(grouped).map(([groupName, phases]) => (
            <SelectGroup key={groupName}>
              <SelectLabel className="text-xs font-semibold text-[#FF6B00] uppercase tracking-wide">
                {groupName}
              </SelectLabel>
              {phases.map((p) => (
                <SelectItem key={p.key} value={p.key} className="py-3"
                  data-testid={`${testIdPrefix}-item-${p.key}`}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
          <SelectGroup>
            <SelectLabel className="text-xs font-semibold text-purple-600 uppercase tracking-wide">
              Personnalisation
            </SelectLabel>
            <SelectItem value={CUSTOM_VALUE} className="py-3 text-purple-700 font-semibold"
              data-testid={`${testIdPrefix}-item-custom`}>
              + Phase personnalisee...
            </SelectItem>
          </SelectGroup>
        </SelectContent>
      </Select>
      {value && !isInCatalog && (
        <Button type="button" onClick={() => { setCustomMode(true); setCustomInput(value); }}
          variant="outline" className="h-14"
          data-testid={`${testIdPrefix}-edit-custom`}
          title="Modifier la phase personnalisee">
          <PencilSimple size={18} />
        </Button>
      )}
    </div>
  );
}

export default PhaseSelector;
